/**
 * Unified tab model. Each open tab is one of:
 *   - { kind: "scope",    payload: <encoded scope path> }   — Explore mode
 *   - { kind: "influence", payload: "<seq>~<turnIndex>~<traceId>" } — Explore mode (influence trace)
 *   - { kind: "studio",   payload: "" }                     — Studio overview
 *   - { kind: "kernel",   payload: <command> }              — Studio kernel detail
 *   - { kind: "canvas",   payload: <slug> }                 — Studio canvas detail
 *   - { kind: "workflow", payload: <command> }              — Studio workflow detail (config + runs)
 *   - { kind: "trigger",  payload: <command> }              — Studio signal detail (trigger + run log)
 *   - { kind: "skills",   payload: "" }                     — Library skills catalog
 *   - { kind: "settings", payload: <admin view id> }        — legacy Admin tab
 *
 * URL encoding: `tabs=<kind>:<payload>;<kind>:<payload>;…&active=<n>`.
 *
 * Back-compat: an unprefixed entry (no `<kind>:` segment) is treated as
 * a scope tab whose payload is the entry itself — matches the previous
 * `tabs=<enc>;<enc>` URL shape.
 */

import { decodeScopePath, encodeScopePath, scopeStepLabel, DIMENSIONS } from "./dimensionDefs.js";

// The tab grammar lives in routeModel.js, which the analytics layer can import.
export {
    TAB_KINDS,
    decodeTab,
    encodeTab,
    legacyStudioTabFromSearchParams,
    makeCanvasTab,
    makeKernelTab,
    makeSettingsTab,
    makeSkillsTab,
    makeStudioTab,
    makeTriggerTab,
    makeWorkflowTab,
    modeForTab,
} from "./routeModel.js";

export function makeScopeTab(scopePath) {
    return { kind: "scope", payload: encodeScopePath(scopePath || []) };
}

// Influence-trace tab (Explore mode). Payload encodes a unique seq, the
// turn index, and the trace id — all URL-serializable, so the tab
// survives reload (the view re-fetches the trace by id). The seq makes
// each open a distinct tab even for the same turn.
export function makeInfluenceTab({ traceId, turnIndex, seq }) {
    return { kind: "influence", payload: `${seq}~${turnIndex}~${traceId || ""}` };
}

export function parseInfluenceTab(tab) {
    if (!tab || tab.kind !== "influence") return null;
    const parts = String(tab.payload || "").split("~");
    if (parts.length < 3) return null;
    const seq = parts[0];
    const turnIndex = Number(parts[1]);
    const traceId = parts.slice(2).join("~");
    if (!traceId || Number.isNaN(turnIndex)) return null;
    return { seq, turnIndex, traceId, payload: tab.payload };
}

// Identity check — used when opening an item to decide "activate
// existing tab" vs "open new tab". Scope tabs compare full encoded
// path; everything else compares payload directly.
export function tabsEqual(a, b) {
    if (!a || !b) return false;
    if (a.kind !== b.kind) return false;
    return (a.payload || "") === (b.payload || "");
}

export function findTabIndex(tabs, target) {
    return tabs.findIndex((t) => tabsEqual(t, target));
}

// Point the tab holding `from` at `to`, keeping its position, and report the
// index it ends up at so the caller can keep it active. Used when an item
// renames itself (a canvas slug edit): opening `to` as a new tab would leave
// the original stranded on an id that no longer resolves. Any other tab
// already parked on `to` is dropped, since the rewrite duplicates it.
// Returns null when no tab holds `from`.
export function renameTab(tabs, from, to) {
    const fromIdx = findTabIndex(tabs, from);
    if (fromIdx < 0) return null;
    const renamed = tabs.map((tab, i) => (i === fromIdx ? to : tab));
    const duplicate = renamed.findIndex((tab, i) => i !== fromIdx && tabsEqual(tab, to));
    if (duplicate < 0) return { tabs: renamed, activeIdx: fromIdx };
    return {
        tabs: renamed.filter((_, i) => i !== duplicate),
        activeIdx: duplicate < fromIdx ? fromIdx - 1 : fromIdx,
    };
}

// Decoded scope path for a scope tab, or null for any other kind.
export function scopePathFor(tab) {
    if (!tab || tab.kind !== "scope") return null;
    return decodeScopePath(tab.payload || "");
}

// Human label for the tab strip. Falls back gracefully when the
// underlying item (e.g. a kernel command) no longer resolves — we
// still show the raw payload so the user can recognize and close it.
export function tabLabel(tab, { adminViews = [] } = {}) {
    if (!tab) return "";
    switch (tab.kind) {
        case "scope": {
            const path = scopePathFor(tab) || [];
            if (!path.length) return "Overview";
            return scopeStepLabel(path[path.length - 1]);
        }
        case "influence": {
            const parsed = parseInfluenceTab(tab);
            return parsed ? `Influence · T${parsed.turnIndex + 1}` : "Influence";
        }
        case "studio":
            return "Library";
        case "kernel":
            return tab.payload ? `/${tab.payload}` : "/";
        case "canvas":
            return tab.payload ? `/${tab.payload}` : "/";
        case "workflow":
            return tab.payload ? `/${tab.payload}` : "/";
        case "trigger":
            return tab.payload ? `/${tab.payload}` : "/";
        case "skills":
            return "Skills";
        case "settings": {
            const def = adminViews.find((v) => v.id === tab.payload);
            return def?.label || tab.payload || "Admin";
        }
        default:
            return tab.payload || "";
    }
}

// Longer hover tooltip — gives a hint about what kind of item this
// tab represents so the bare label isn't ambiguous (especially when a
// kernel and canvas share a similar slug).
export function tabTitle(tab, opts = {}) {
    const label = tabLabel(tab, opts);
    switch (tab?.kind) {
        case "scope": {
            const path = scopePathFor(tab) || [];
            if (!path.length) return "Overview";
            const last = path[path.length - 1];
            const dimLabel = DIMENSIONS[last.dim]?.label || last.dim;
            return `${dimLabel}: ${label}`;
        }
        case "influence": {
            const parsed = parseInfluenceTab(tab);
            return parsed ? `Influence trace · Turn ${parsed.turnIndex + 1}` : "Influence trace";
        }
        case "studio":   return "Library · Overview";
        case "kernel":   return `Kernel · ${label}`;
        case "canvas":   return `Canvas · ${label}`;
        case "workflow": return `Workflow · ${label}`;
        case "trigger":  return `Signal · ${label}`;
        case "skills":   return "Library · Skills";
        case "settings": return `Admin · ${label}`;
        default:         return label;
    }
}
